import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import type { DropdownOption } from '@/hooks/useDropdownOptions'

/**
 * Dashboard filter bar — Stories 4.7a (visible-disabled MVP) + 4.7b (active).
 *
 * 4.7a shipped with controls in `disabled` state and microcopy "Filtros em
 * breve" — preserved the visual slot without the wiring. 4.7b enables the
 * controls, lifts state to the parent (Dashboard.tsx), and removes the
 * microcopy.
 *
 * Period preset semantics:
 *   - `today`  → `from = today UTC midnight`, `to = now`
 *   - `week`   → `from = 7 days ago at UTC midnight`
 *   - `month`  → `from = first day of current month UTC` (default)
 *   - `custom` → caller controls `from`/`to` directly via the date inputs
 */
export type PeriodPreset = 'today' | 'week' | 'month' | 'custom'

/**
 * `SellerOption`/`CampaignOption` used to be two byte-for-byte identical
 * `{id, name}` interfaces declared here — QA follow-up (2026-07-16):
 * both dropdowns now share `DropdownOption` from `useDropdownOptions`.
 */
export type SellerOption = DropdownOption
export type CampaignOption = DropdownOption
/** Story 12.10 (Epic E12) */
export type SdrOption = DropdownOption

export interface DashboardFiltersValue {
  preset: PeriodPreset
  /** Only consumed when `preset === 'custom'`. ISO yyyy-mm-dd, no time. */
  customFrom: string
  /** Only consumed when `preset === 'custom'`. ISO yyyy-mm-dd, no time. */
  customTo: string
  /** Empty string means "all sellers". */
  sellerId: string
  /** Empty string means "all campaigns". */
  campaignId: string
  /**
   * Story 12.10 (Epic E12) — empty string means "all SDRs" (or, for a
   * `role='sdr'` session, is irrelevant: the backend forces her own
   * carteira regardless of this value, same clamp precedent as `sellerId`
   * for `vendedor`).
   */
  sdrId: string
}

export interface DashboardFiltersProps {
  value: DashboardFiltersValue
  onChange: (next: DashboardFiltersValue) => void
  sellers: ReadonlyArray<SellerOption>
  sellersLoading?: boolean
  campaigns: ReadonlyArray<CampaignOption>
  campaignsLoading?: boolean
  /**
   * Story 12.10 (Epic E12) — the SDR dropdown is a management-only filter
   * (mirrors the AC3 decision: showing it to a `sdr`/`vendedor`/`operator`/
   * `viewer` session would be a no-op or actively confusing, since only
   * admin/manager can pick a DIFFERENT carteira than their own). Computed by
   * the parent from `role`, not by this component — `DashboardFilters` has
   * no auth context of its own, same posture as every other prop here.
   */
  showSdrFilter?: boolean
  sdrs?: ReadonlyArray<SdrOption>
  sdrsLoading?: boolean
}

const PRESET_LABELS: Record<PeriodPreset, string> = {
  today: 'Hoje',
  week: 'Semana',
  month: 'Este mês',
  custom: 'Custom',
}
const PRESETS: ReadonlyArray<PeriodPreset> = ['today', 'week', 'month', 'custom']

export function DashboardFilters({
  value,
  onChange,
  sellers,
  sellersLoading = false,
  campaigns,
  campaignsLoading = false,
  showSdrFilter = false,
  sdrs = [],
  sdrsLoading = false,
}: DashboardFiltersProps): React.ReactElement {
  const setPreset = (preset: PeriodPreset): void => {
    onChange({ ...value, preset })
  }
  const setSellerId = (sellerId: string): void => {
    onChange({ ...value, sellerId })
  }
  const setCampaignId = (campaignId: string): void => {
    onChange({ ...value, campaignId })
  }
  const setSdrId = (sdrId: string): void => {
    onChange({ ...value, sdrId })
  }
  const setCustomFrom = (customFrom: string): void => {
    onChange({ ...value, customFrom })
  }
  const setCustomTo = (customTo: string): void => {
    onChange({ ...value, customTo })
  }

  return (
    <div className="rounded-md border bg-muted/40 p-4" data-testid="dashboard-filters">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-muted-foreground">Período</label>
          <div className="flex gap-1.5">
            {PRESETS.map((preset) => (
              <Button
                key={preset}
                type="button"
                variant={value.preset === preset ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPreset(preset)}
                aria-pressed={value.preset === preset}
                data-testid={`preset-${preset}`}
              >
                {PRESET_LABELS[preset]}
              </Button>
            ))}
          </div>
        </div>

        {value.preset === 'custom' && (
          <div className="flex items-end gap-2" data-testid="custom-period">
            <div className="flex flex-col gap-1.5">
              <label
                className="text-xs font-medium text-muted-foreground"
                htmlFor="custom-from"
              >
                De
              </label>
              <input
                id="custom-from"
                type="date"
                value={value.customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label
                className="text-xs font-medium text-muted-foreground"
                htmlFor="custom-to"
              >
                Até
              </label>
              <input
                id="custom-to"
                type="date"
                value={value.customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="seller-filter">
            Vendedor
          </label>
          <Select
            id="seller-filter"
            value={value.sellerId}
            onChange={(e) => setSellerId(e.target.value)}
            className="w-56"
            disabled={sellersLoading}
            data-testid="seller-select"
          >
            <option value="">Todos os vendedores</option>
            {sellers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="campaign-filter">
            Campanha
          </label>
          <Select
            id="campaign-filter"
            value={value.campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            className="w-56"
            disabled={campaignsLoading}
            data-testid="campaign-select"
          >
            <option value="">Todas as campanhas</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>

        {showSdrFilter && (
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-muted-foreground" htmlFor="sdr-filter">
              SDR
            </label>
            <Select
              id="sdr-filter"
              value={value.sdrId}
              onChange={(e) => setSdrId(e.target.value)}
              className="w-56"
              disabled={sdrsLoading}
              data-testid="sdr-select"
            >
              <option value="">Todas as SDRs</option>
              {sdrs.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>
    </div>
  )
}

export default DashboardFilters
